import { get, flatten } from 'lodash';
import moment from 'moment';
import { calculateCost } from '../../project-manager/components/ProjectFinancialsDetailsTable';

export const formatDate = dateStr => {
  let dateString = dateStr;
  let year = dateString.substring(0, 4);
  let month = dateString.substring(4, 6);
  let day = dateString.substring(6, 8);
  let date = new Date(year, month - 1, day);
  return date.toLocaleDateString('fr-CA');
};

// Helper function to check if a deliverable is PTO
export const isPTO = deliverableName => {
  if (!deliverableName) return false;
  const name = deliverableName.trim();
  return name === 'Paid Time Off';
};

// Process PTO allocation based on department
export const processPTOAllocation = (allRows, projectClassifications) => {
  // Group rows by user email
  const userGroups = {};
  
  allRows.forEach(row => {
    const email = row.email;
    if (!email) return;
    
    if (!userGroups[email]) {
      userGroups[email] = {
        user: row,
        ptoRows: [],
        projectRows: [],
      };
    }
    
    // Separate PTO entries from project work
    if (isPTO(row.deliverable)) {
      userGroups[email].ptoRows.push(row);
    } else {
      userGroups[email].projectRows.push(row);
    }
  });
  
  const result = [];
  
  // Process each user
  Object.values(userGroups).forEach(({ user, ptoRows, projectRows }) => {
    const department = user.department || '';
    const totalPTOHours = ptoRows.reduce((sum, row) => sum + (row.hours || 0), 0);
    
    // Logic 1: If department != "General Projects", assign all PTO to department
    if (department !== 'General Projects') {
      // Calculate total hours (project work + PTO) for allocation percentages
      const totalProjectHours = projectRows.reduce((sum, row) => sum + (row.hours || 0), 0);
      const totalAllHours = totalProjectHours + totalPTOHours;
      
      // Aggregate project rows by projectId
      const projectAggregates = {};
      projectRows.forEach(row => {
        const projectId = row.projectId;
        if (!projectId) return;
        
        if (!projectAggregates[projectId]) {
          // Use the first row for this project as the base to preserve overrides
          projectAggregates[projectId] = {
            ...row,
            hours: 0,
          };
        }
        projectAggregates[projectId].hours += row.hours || 0;
      });
      
      // Add aggregated project rows with allocation percentage
      Object.values(projectAggregates).forEach(agg => {
        const allocation = totalAllHours > 0 ? (agg.hours / totalAllHours) * 100 : 0;
        result.push({
          ...agg,
          projectCodeAdj: agg.projectCodeAdj || agg.projectCode,
          classificationAdj: agg.classificationAdj || agg.classification,
          total: agg.hours,
          allocation: allocation,
        });
      });
      
      // Add PTO row assigned to department
      if (totalPTOHours > 0) {
        const allocation = totalAllHours > 0 ? (totalPTOHours / totalAllHours) * 100 : 0;
        result.push({
          ...user,
          projectCodeAdj: user.projectCodeAdj || department,
          classificationAdj: user.classificationAdj || '',
          total: totalPTOHours,
          allocation: allocation,
          deliverable: 'Paid Time Off',
          project: '',
          projectCode: '',
          projectId: null,
        });
      }
    } else {
      // Logic 2: If department == "General Projects", calculate project allocation
      const totalProjectHours = projectRows.reduce((sum, row) => sum + (row.hours || 0), 0);
      
      if (totalProjectHours === 0) {
        // No project hours, assign all PTO to department
        if (totalPTOHours > 0) {
          result.push({
            ...user,
            projectCodeAdj: user.projectCodeAdj || department,
            classificationAdj: user.classificationAdj || '',
            total: totalPTOHours,
            allocation: 100.0,
            deliverable: 'Paid Time Off',
            project: '',
            projectCode: '',
            projectId: null,
          });
        }
        return;
      }
      
      // Group project rows by projectId to calculate allocation percentages
      const projectGroups = {};
      projectRows.forEach(row => {
        const projectId = row.projectId;
        if (!projectId) return;
        
        if (!projectGroups[projectId]) {
          projectGroups[projectId] = {
            projectId,
            projectCode: row.projectCode || '',
            classification: projectClassifications[projectId] || '',
            hours: 0,
          };
        }
        projectGroups[projectId].hours += row.hours || 0;
      });
      
      // Calculate allocation percentages
      const projectAllocations = Object.values(projectGroups).map(proj => ({
        ...proj,
        percentage: (proj.hours / totalProjectHours) * 100,
      }));
      
      // Add aggregated project rows with allocation
      projectAllocations.forEach(projAlloc => {
        // Find a representative row for this project to preserve overrides
        const representativeRow = projectRows.find(r => r.projectId === projAlloc.projectId) || user;
        result.push({
          ...representativeRow,
          projectCodeAdj: representativeRow.projectCodeAdj || projAlloc.projectCode,
          classificationAdj: representativeRow.classificationAdj || projAlloc.classification,
          total: projAlloc.hours,
          allocation: projAlloc.percentage,
          projectId: projAlloc.projectId,
          projectCode: projAlloc.projectCode,
        });
      });
      
      // Split PTO hours based on project allocation percentages
      projectAllocations.forEach(projAlloc => {
        const ptoHoursForProject = (totalPTOHours * projAlloc.percentage) / 100;
        
        if (ptoHoursForProject > 0) {
          // Find a representative row for this project to preserve overrides
          const representativeRow = projectRows.find(r => r.projectId === projAlloc.projectId) || user;
          result.push({
            ...representativeRow,
            projectCodeAdj: representativeRow.projectCodeAdj || projAlloc.projectCode,
            classificationAdj: representativeRow.classificationAdj || projAlloc.classification,
            total: ptoHoursForProject,
            allocation: projAlloc.percentage,
            deliverable: 'Paid Time Off',
            project: '',
            projectCode: projAlloc.projectCode,
            projectId: projAlloc.projectId,
          });
        }
      });
    }
  });
  
  return result;
};

export const createGridData = timesheets =>
  flatten(
    timesheets.map(ts =>
      ts.lineItems.map(li => {
        let bu = `${get(li, 'project.accountingCode') || ''}-${
          get(li, 'project.accountingCodeSuffix') || ''
        }`;
        if (get(li, 'deliverable.accountingCode') && get(li, 'deliverable.accountingCodeSuffix')) {
          bu = `${get(li, 'deliverable.accountingCode') || ''}-${
            get(li, 'deliverable.accountingCodeSuffix') || ''
          }`;
        }
        let days = ['monTask', 'tueTask', 'wedTask', 'thuTask', 'friTask', 'satTask', 'sunTask'];
        let finalData = [];
        for (let i = 0; i < days.length; i++) {
          const userRole = get(ts, 'user.role');
          const email = get(ts, 'user.emails[0].address');
          const projectRoles = get(li, 'project.roles') || [];
          const resources = get(li, 'project.resources') || [];
          const resourceRole = resources.find(r => r.email === email)?.role;
          const role = resourceRole || userRole;
          const defaultRate = get(ts, 'user.defaultRate');
          const status = get(ts, 'status');
          const approvers =
            status === 'Approved'
              ? get(ts, 'approvalsRequired')
                  ?.map(a => a.approvedBy)
                  ?.join(', ') || undefined
              : undefined;
          const approvedOn =
            status === 'Approved'
              ? get(ts, 'approvalsRequired')?.sort(
                  (a, b) => new Date(b.approvedOn) - new Date(a.approvedOn),
                )[0]?.approvedOn
              : undefined;

          const clientName = get(li, 'project.client.shortName');
          let temp = {
            timesheetId: get(ts, '_id'),
            status,
            endDate: moment(get(ts, 'endDate')).format('MM-DD-YYYY'),
            name: `${get(ts, 'user.firstName')} ${get(ts, 'user.lastName')}`,
            firstName: get(ts, 'user.firstName'),
            lastName: get(ts, 'user.lastName'),
            role,
            payrollId: get(ts, 'user.payrollId'),
            email: get(ts, 'user.emails[0].address'),
            clientName,
            project: get(li, 'project.name'),
            projectCode: get(li, 'project.projectCode'),
            roles: get(li, 'project.roles')?.length || 0,
            bu: bu,
            milestone: get(li, 'deliverable.milestoneName'),
            deliverable: get(li, 'deliverable.deliverableName'),
            supervisor: get(ts, 'user.supervisor.firstName')
              ? get(ts, 'user.supervisor.firstName') + ' ' + get(ts, 'user.supervisor.lastName')
              : undefined,
            approvedOn: approvedOn
              ? moment(approvedOn).format('MM-DD-YYYY')
              : status === 'Approved'
              ? moment(get(ts, 'updatedAt')).format('MM-DD-YYYY')
              : undefined,
            approvers,
            department: get(ts, 'user.department'),
            projectId: get(li, 'project._id'),
          };
          let taskdate = get(li, days[i] + '.date');
          let taskhour = get(li, days[i] + '.hours');
          let comment = get(li, days[i] + '.comment');

          if (taskdate && taskhour) {
            const totalCost = calculateCost({
              email,
              hours: taskhour,
              resources,
              roles: projectRoles,
              userRole,
              defaultRate,
            });

            temp['taskdate'] = formatDate(taskdate);
            temp['hours'] = taskhour;
            temp['comment'] = comment;
            temp['totalCost'] = totalCost;
            temp['rate'] = totalCost / taskhour;

            finalData.push(temp);
          }
        }
        return finalData;
      }),
    ),
  );

